import { funnelDetailModel } from '../../../../../models/funnelDetailModel';
import SweepStagesModel from '../../../../../models/stagesModel';
import { stageToConnectStepItem, isSalesforceLink, isThirdPartyLink } from './helper';
import { colors } from '@sweep-io/sweep-design';
import { ConnectStepsListGroup, SfCurrentStep } from './types';
import { FunnelType } from '../../../../../types/enums/FunnelType';
import { CONNECT_STEPS_TAGS_COLORS } from './consts';
import { translateTypeToHumanName } from '../../../../documentation/helpers';

export const getGroupsForSalesforceStep = ({
  step,
  funnelsData,
  thirdPartyFunnelsData,
  metadataElementsData,
  getObjectTypeLabel,
}: {
  step: SfCurrentStep;
  funnelsData: FunnelMap['funnelsData'];
  thirdPartyFunnelsData: FunnelMap['thirdPartyFunnelsData'];
  metadataElementsData: FunnelMap['metadataElementsData'];
  getObjectTypeLabel: (crmOrgId: string, objectName: string) => string;
}) => {
  const sfFunnel = funnelsData[step.funnelId];
  if (!step.stageId) {
    console.error('No stageId provided for third party step');
    return [];
  }

  const sfStep = sfFunnel.funnelDetails.stages.find((s) => s._stageId === step.stageId);

  if (!sfFunnel || !sfStep) {
    return [];
  }
  const currentFunnelId = step.funnelId;
  const objectNameLabel = getObjectTypeLabel(
    sfFunnel.crmOrgId,
    sfFunnel.funnelDetails.leadingObject.objectName,
  );

  const currentFunnelGroup: ConnectStepsListGroup = {
    value: funnelsData[currentFunnelId].id,
    parentName: funnelsData[currentFunnelId].name,
    label: objectNameLabel,
    funnelType: FunnelType.SALESFORCE,
    items: funnelDetailModel(funnelsData[currentFunnelId].funnelDetails)
      .getAllStageValidConnections(step.stageId)
      .map(stageToConnectStepItem),
    tag: {
      type: 'object',
      objectName: objectNameLabel,
    },
  };

  const stageLinks: FunnelLink[] | undefined = sfStep.funnelLinks;

  const doesNotHaveSfStageFunnelLink = (funnelId: string) => (stage: SweepStage) => {
    return !Boolean(
      stageLinks
        ?.filter(isSalesforceLink)
        .find((link) => link.funnelId === funnelId && stage._stageId === link.stageId),
    );
  };

  const doesNotHaveThirdPartyStageFunnelLink = (funnelId: string) => (step: ThirdPartyStep) => {
    return !Boolean(
      stageLinks
        ?.filter(isThirdPartyLink)
        .find((link) => link.funnelId === funnelId && step.id === link.stageId),
    );
  };
  const hasStepToFunnelLink = (funnelId: string) => {
    return Boolean(sfStep.funnelLinks?.some((link) => link.funnelId === funnelId && !link.stageId));
  };

  const allOtherSfFunnelGroups: ConnectStepsListGroup[] = Object.keys(funnelsData)
    .filter((funnelId) => funnelId !== currentFunnelId)
    .map((funnelId) => {
      const objectNameLabel = getObjectTypeLabel(
        funnelsData[funnelId].crmOrgId,
        funnelsData[funnelId].funnelDetails.leadingObject.objectName,
      );
      return {
        value: funnelsData[funnelId].id,
        parentName: funnelsData[funnelId].name,
        label: objectNameLabel,
        objectName: funnelsData[funnelId].funnelDetails.leadingObject.objectName,
        selectable: !hasStepToFunnelLink(funnelId),
        funnelType: FunnelType.SALESFORCE,
        items: new SweepStagesModel(funnelsData[funnelId].funnelDetails.stages)
          .nonLostSteps()
          .filter(doesNotHaveSfStageFunnelLink(funnelId))
          .map(stageToConnectStepItem),
        tag: {
          type: 'object',
          objectName: objectNameLabel,
        },
      };
    });

  const allOtherThirdPartyFunnelGroups: ConnectStepsListGroup[] = Object.keys(thirdPartyFunnelsData)
    .filter((funnelId) => funnelId !== currentFunnelId)
    .map((funnelId) => ({
      value: thirdPartyFunnelsData[funnelId].id,
      parentName: thirdPartyFunnelsData[funnelId].name,
      label: '3rd party integration',
      objectName: '',
      labelColor: colors.storm[200],
      selectable: !hasStepToFunnelLink(funnelId),
      funnelType: FunnelType.THIRD_PARTY,
      items: thirdPartyFunnelsData[funnelId].steps
        .filter(doesNotHaveThirdPartyStageFunnelLink(funnelId))
        .map((step) => ({
          value: step.id,
          label: step.name,
        })),
      tag: {
        type: 'other',
        tagText: '3rd party integration',
        tagColor: CONNECT_STEPS_TAGS_COLORS.thirdPartyIntegration,
      },
    }));

  const hasFunnelLinkToMetadataElement = (metadataId: string) => {
    return Boolean(
      sfStep.funnelLinks?.some(
        (link) => link._type === FunnelType.METADATA_ELEMENT && link.funnelId === metadataId,
      ),
    );
  };

  const metadataElementsGroups: ConnectStepsListGroup[] = Object.entries(
    metadataElementsData || {},
  ).map(([metadataId, metadataElement]) => ({
    value: metadataId,
    parentName: metadataElement.name,
    label: 'Metadata element',
    objectName: metadataElement.name,
    selectable: !hasFunnelLinkToMetadataElement(metadataId),
    funnelType: FunnelType.METADATA_ELEMENT,
    items: [],
    tag: {
      type: 'other',
      tagText: translateTypeToHumanName(metadataElement.type),
      tagColor: CONNECT_STEPS_TAGS_COLORS.metadataElement,
    },
  }));

  return [
    currentFunnelGroup,
    ...allOtherSfFunnelGroups,
    ...allOtherThirdPartyFunnelGroups,
    ...metadataElementsGroups,
  ];
};

export const getGroupsForSalesforceFunnel = ({
  step,
  funnelsData,
  thirdPartyFunnelsData,
  metadataElementsData,
  getObjectTypeLabel,
}: {
  step: SfCurrentStep;
  funnelsData: FunnelMap['funnelsData'];
  thirdPartyFunnelsData: FunnelMap['thirdPartyFunnelsData'];
  metadataElementsData: FunnelMap['metadataElementsData'];
  getObjectTypeLabel: (crmOrgId: string, objectName: string) => string;
}) => {
  if (step.stageId !== undefined) {
    console.log(
      'Unexpected. Stage Id provided in getGroupsForThirdPartyFunnel. This isn\t a funnel.',
    );
    return [];
  }
  const sfFunnel = funnelsData[step.funnelId].funnelDetails;
  if (!sfFunnel) {
    return [];
  }
  const currentFunnelId = step.funnelId;

  const funnelLinks: FunnelLink[] | undefined = sfFunnel.funnelLinks;

  const doesNotHaveSfStageFunnelLink = (funnelId: string) => (stage: SweepStage) => {
    return !Boolean(
      sfFunnel.funnelLinks
        ?.filter(isSalesforceLink)
        .find((link) => link.funnelId === funnelId && stage._stageId === link.stageId),
    );
  };

  const doesNotHaveThirdPartyStageFunnelLink = (funnelId: string) => (step: ThirdPartyStep) => {
    return !Boolean(
      funnelLinks
        ?.filter(isThirdPartyLink)
        .find((link) => link.funnelId === funnelId && step.id === link.stageId),
    );
  };
  const hasStepToFunnelLink = (funnelId: string) => {
    return Boolean(
      sfFunnel.funnelLinks?.some((link) => link.funnelId === funnelId && !link.stageId),
    );
  };

  const allOtherSfFunnelGroups: ConnectStepsListGroup[] = Object.keys(funnelsData)
    .filter((funnelId) => funnelId !== currentFunnelId)
    .map((funnelId) => {
      const objectNameLabel = getObjectTypeLabel(
        funnelsData[funnelId].crmOrgId,
        funnelsData[funnelId].funnelDetails.leadingObject.objectName,
      );
      return {
        value: funnelsData[funnelId].id,
        parentName: funnelsData[funnelId].name,
        label: objectNameLabel,
        selectable: !hasStepToFunnelLink(funnelId),
        funnelType: FunnelType.SALESFORCE,
        items: new SweepStagesModel(funnelsData[funnelId].funnelDetails.stages)
          .nonLostSteps()
          .filter(doesNotHaveSfStageFunnelLink(funnelId))
          .map(stageToConnectStepItem),
        tag: {
          type: 'object',
          objectName: objectNameLabel,
        },
      };
    });

  const allOtherThirdPartyFunnelGroups: ConnectStepsListGroup[] = Object.keys(thirdPartyFunnelsData)
    .filter((funnelId) => funnelId !== currentFunnelId)
    .map((funnelId) => ({
      value: thirdPartyFunnelsData[funnelId].id,
      parentName: thirdPartyFunnelsData[funnelId].name,
      label: '3rd party integration',
      selectable: !hasStepToFunnelLink(funnelId),
      funnelType: FunnelType.THIRD_PARTY,
      items: thirdPartyFunnelsData[funnelId].steps
        .filter(doesNotHaveThirdPartyStageFunnelLink(funnelId))
        .map((step) => ({
          value: step.id,
          label: step.name,
        })),
      tag: {
        type: 'other',
        tagText: '3rd party integration',
        tagColor: CONNECT_STEPS_TAGS_COLORS.thirdPartyIntegration,
      },
    }));

  const hasFunnelLinkToMetadataElement = (metadataId: string) => {
    return Boolean(
      sfFunnel.funnelLinks?.some(
        (link) => link._type === FunnelType.METADATA_ELEMENT && link.funnelId === metadataId,
      ),
    );
  };

  const metadataElementsGroups: ConnectStepsListGroup[] = Object.entries(
    metadataElementsData || {},
  ).map(([metadataId, metadataElement]) => ({
    value: metadataId,
    parentName: metadataElement.name,
    label: 'Metadata element',
    objectName: metadataElement.name,
    selectable: !hasFunnelLinkToMetadataElement(metadataId),
    funnelType: FunnelType.METADATA_ELEMENT,
    items: [],
    tag: {
      type: 'other',
      tagText: translateTypeToHumanName(metadataElement.type),
      tagColor: CONNECT_STEPS_TAGS_COLORS.metadataElement,
    },
  }));

  return [...allOtherSfFunnelGroups, ...allOtherThirdPartyFunnelGroups, ...metadataElementsGroups];
};
